// Colonne de droite de l'écran S12 : l'emplacement sélectionné sur le plan (type, surface, zone,
// état et motif, attribution en cours), ses actions (fiche, attribuer, renouveler, résilier) et
// l'invitation à choisir un emplacement quand rien n'est sélectionné.
import { LienBouton, Panneau } from '@cc/ui';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { date } from '../../commun/format';
import { aujourdhuiIso, LIBELLES_SITUATION_EMPLACEMENT, situationEmplacement } from './logique';
import type { Emplacement } from './types';
import styles from './marches.module.css';

export function PanneauEmplacement({ emplacement: e, aujourdhui = aujourdhuiIso() }: { emplacement: Emplacement | null; aujourdhui?: string }) {
  const { contexte } = useSession();
  const peutAttribuer = peut(contexte, 'marche_municipal', 'creer');
  if (!e) {
    return (
      <Panneau titre="Emplacement sélectionné" niveau={2}>
        <p className={styles.aide}>Choisissez un emplacement sur le plan (clic, ou Tabulation puis Entrée) pour voir sa situation et agir.</p>
      </Panneau>
    );
  }
  const situation = situationEmplacement(e, aujourdhui);
  const attribuable = e.etat === 'disponible';
  return (
    <Panneau titre={`Emplacement ${e.reference}`} niveau={2} id="emplacement-selectionne">
      <p className={styles.aide}>
        {e.type_libelle} · {e.surface_m2.toLocaleString('fr-FR')} m² · {e.zone.id ? `zone ${e.zone.code ?? '?'}` : 'sans zone'}
        {e.zone.nom ? ` (${e.zone.nom})` : ''}
      </p>
      <dl className={styles.definitions}>
        <dt>Situation</dt>
        <dd>
          <span className={situation === 'expire_bientot' ? styles.attention : situation === 'attribue' ? styles.ok : undefined}>{LIBELLES_SITUATION_EMPLACEMENT[situation]}</span>
          {e.etat_motif ? ` : ${e.etat_motif}` : ''}
        </dd>
        {e.attribution_en_cours ? (
          <>
            <dt>Occupant</dt>
            <dd>{e.attribution_en_cours.commercant}</dd>
            <dt>Attribution</dt>
            <dd>
              <span className="cc-mono">{e.attribution_en_cours.numero}</span>
              {e.attribution_en_cours.fin ? `, jusqu'au ${date(e.attribution_en_cours.fin)}` : ''}
            </dd>
          </>
        ) : null}
        <dt>Rubrique</dt>
        <dd className="cc-mono">{e.rubrique_code}</dd>
        {e.observations ? (
          <>
            <dt>Observations</dt>
            <dd>{e.observations}</dd>
          </>
        ) : null}
      </dl>
      <div className={styles.actions}>
        <LienBouton href={`/marches/emplacements/${e.id}`} variante="secondaire">
          Fiche et tarification
        </LienBouton>
        {attribuable && peutAttribuer ? (
          <LienBouton href={`/marches/attributions/nouvelle?emplacement=${e.id}`} variante="principal">
            Attribuer
          </LienBouton>
        ) : null}
        {e.attribution_en_cours ? (
          <LienBouton href={`/marches/attributions/${e.attribution_en_cours.id}`} variante="secondaire">
            Attribution en cours
          </LienBouton>
        ) : null}
      </div>
      {!attribuable && !e.attribution_en_cours ? <p className={styles.aide}>Un emplacement réservé ou hors service ne peut pas être attribué : changez son état depuis sa fiche.</p> : null}
    </Panneau>
  );
}
